import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import * as Sentry from '@sentry/react'
import {
  catalogSyncedEndpointSelector,
  outletSelector,
  posZeroLoadingSelector,
  warehousesHasRoleSelector,
} from 'app-redux/selectors'
import { syncCatalog } from 'utils/posLocalDb/catalogSync'
import {
  getActiveEndpoint,
  setLocalCatalogReady,
  setStockWarehouseIds,
} from 'utils/posLocalDb/queueContext'

/** Jeda minimal antar-sync delta otomatis (fokus tab bisa sering terjadi). */
const MIN_INTERVAL_MS = 60_000

/**
 * Sinkronisasi turun katalog lokal di latar belakang (Mode Zero-Loading): saat mount,
 * saat tab kembali aktif, dan saat koneksi pulih. Hanya jalan bila mode ON dan gate
 * sync pertama tenant ini sudah lolos. Non-blocking — UI tetap membaca data lokal.
 */
const usePosCatalogSync = () => {
  const enabled = useSelector(posZeroLoadingSelector)
  const syncedEndpoint = useSelector(catalogSyncedEndpointSelector)
  const lastRunRef = useRef(0)
  const outlet = useSelector(outletSelector)
  const warehouses = useSelector(warehousesHasRoleSelector)
  const isReady = enabled && !!syncedEndpoint && syncedEndpoint === getActiveEndpoint()
  const readyRef = useRef(isReady)
  readyRef.current = isReady

  // Gudang outlet + rak-raknya = target stok baris cart (sama dengan alur edit order).
  const stockWarehouseIds = useMemo(() => {
    const outletWarehouseId = outlet?.warehouse?.id ?? outlet?.warehouse_id
    const rackIds = (warehouses || [])
      .filter((row) => outletWarehouseId && row.parent_warehouse_id === outletWarehouseId)
      .map((row) => row.id)
    return [outletWarehouseId, ...rackIds].filter(Boolean)
  }, [outlet?.warehouse?.id, outlet?.warehouse_id, warehouses])

  // Disetel saat render (bukan effect) supaya wrapper detail produk langsung tahu
  // sumber data yang dipakai begitu kasir dirender.
  setLocalCatalogReady(isReady)
  setStockWarehouseIds(stockWarehouseIds)

  const sync = useCallback((force = false) => {
    if (!readyRef.current) {
      return
    }
    const now = Date.now()
    if (!force && now - lastRunRef.current < MIN_INTERVAL_MS) {
      return
    }
    lastRunRef.current = now
    syncCatalog(getActiveEndpoint()).catch((error) => Sentry.captureException(error))
  }, [])

  useEffect(() => {
    if (isReady) {
      sync(true)
    }
  }, [isReady, sync])

  useEffect(() => {
    const handleVisible = () => {
      if (document.visibilityState === 'visible') {
        sync()
      }
    }
    const handleOnline = () => sync(true)
    document.addEventListener('visibilitychange', handleVisible)
    window.addEventListener('online', handleOnline)
    return () => {
      document.removeEventListener('visibilitychange', handleVisible)
      window.removeEventListener('online', handleOnline)
    }
  }, [sync])

  return { sync }
}

export default usePosCatalogSync
